import { TimeZone } from "client/DateAndTime/timezones";
import { useForceRerender } from "./useForceRerender";
import { useEffect, useMemo } from "react";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { convertToTimeOnly } from "client/DateAndTime/timeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import {
	millisecondsInAMinute,
	millisecondsInAnHour,
	millisecondsInASecond
} from "client/DateAndTime/dateTimeUtils";

export function useCurrentHour(timezone: TimeZone): number {
	const [rerender, version] = useForceRerender();

	const { currentHour, expiresAfterMs } = useMemo(
		(): { currentHour: number; expiresAfterMs: number; } => {
			hasImplicitDependencyOn(version);

			const now = new Date();

			const time: TimeOnly = convertToTimeOnly(
				now,
				timezone
			);

			const msPassedThisHour: number = (time.minute * millisecondsInAMinute)
				+ (time.second * millisecondsInASecond)
				+ time.millisecond;

			const msTilNextHour: number = millisecondsInAnHour - msPassedThisHour;

			return { currentHour: time.hour, expiresAfterMs: msTilNextHour };
		},
		[version, timezone]
	);

	useEffect(
		(): (() => void) => {
			const timeoutHandle: SetTimeoutHandle = setTimeout(rerender, expiresAfterMs);
			return (): void => clearTimeout(timeoutHandle);
		},
		[expiresAfterMs, rerender]
	);

	return currentHour;
}
